import { Injectable } from '@angular/core';
import { forkJoin, of, switchMap, throwError } from 'rxjs';
import { AppConfig } from 'src/app/app.config';
import { AlfrescoService } from 'src/app/shared/_services/alfresco.service';
import { OpenTelemetryService } from 'src/app/shared/_services/open-telemetry.service';
import { ToastService } from 'src/app/shared/_services/toast.service';
import { WSService } from 'src/app/shared/_services/ws.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
	alfrescoApiUrl: string;
	alfrescoAuthUrl = '/authentication/versions/1';
	alfrescoCoreUrl = '/alfresco/versions/1';
	alfrescoCmisUrl = '/cmis/versions/1.1/browser/root';
	classeRadiceAlfresco: string;
	limiteScadenzaPassword: number;
	regexPassword: string;
	messaggioErroreRegexLogin: string;

	constructor(
		private alfrescoService: AlfrescoService,
		private wsService: WSService,
		private openTelemetryService: OpenTelemetryService,
		private toastService: ToastService,
		appConfig: AppConfig,
	) {
		this.classeRadiceAlfresco = appConfig.get('classeRadiceAlfresco');
		this.alfrescoApiUrl = appConfig.get('alfrescoApiUrl');
		this.limiteScadenzaPassword = appConfig.get('limiteScadenzaPassword');
		this.regexPassword = appConfig.get('regexPassword');
		this.messaggioErroreRegexLogin = appConfig.get('messaggioErroreRegexLogin');
	}

	login(userId: string, password: string, saltaControlloScadenzaPassword = false) {
		return this.alfrescoService.login(userId, password).pipe(
			/* Eseguo le chimate per recupare i dati dell' utente, dei siti, delle classi documentali e dei permessi  */
			switchMap((ticketResponse: any) => {
				localStorage.setItem('alfresco-ticket', ticketResponse.entry.id);

				const $chiamateAlfrescoDatiUtente = [
					this.alfrescoService.getUtenteLoggato(),
					this.alfrescoService.getSiti(),
					this.alfrescoService.getClassiDocumentali(),
					this.alfrescoService.getGruppiUtenteLoggato(),
				];
				return forkJoin($chiamateAlfrescoDatiUtente);
			}),

			/* Salvo i dati in storage e recupero datalist e configurazioni per il sito selezionato */
			switchMap(([resUtente, resSiti, resClassiDocumentali, resGruppi]: any) => {
				const idSitoSelezionato = resSiti.list.entries[0].entry.id;
				const ruoloSitoAttivo = resSiti.list.entries[0].entry.role;
				const classiDocuementali = resClassiDocumentali.list.entries
					.map((element: any) => element.entry)
					.sort((a: any, b: any) => a.title?.localeCompare(b?.title));
				const permessi = resGruppi.list.entries.map(({ entry }: any) => entry.id);

				localStorage.setItem('alfresco-user', JSON.stringify(resUtente.entry));
				localStorage.setItem('alfresco-sites', JSON.stringify(resSiti.list.entries));
				localStorage.setItem('sito-attivo', idSitoSelezionato);
				localStorage.setItem('ruolo-sito-attivo', ruoloSitoAttivo);
				localStorage.setItem('alfresco-classi-documentali', JSON.stringify(classiDocuementali));
				localStorage.setItem('alfresco-groups', JSON.stringify(permessi));

				this.openTelemetryService.attributiFissi.idUtente = resUtente.entry.id;

				return forkJoin([
					this.wsService.getConfigurazioniTenant(idSitoSelezionato),
					this.wsService.getDomini(idSitoSelezionato),
					this.alfrescoService.getUtentiSito(idSitoSelezionato),
				]);
			}),

			/* Salvo i datalist in storage e controllo la scadenza della password */
			switchMap(([configurazioniTenant, resGetDomini, resUtentiSito]: any) => {
				localStorage.setItem('alfresco-datalists', JSON.stringify(resGetDomini.result));
				localStorage.setItem('configurazioni-tenant', JSON.stringify(configurazioniTenant.result));

				const ruoloSitoAttivo = localStorage.getItem('ruolo-sito-attivo') || '';
				if (configurazioniTenant.result.visibilitaPersonaleDocumentiUtente && ruoloSitoAttivo !== 'SiteManager') {
					localStorage.setItem('alfresco-utenti-sito', JSON.stringify([JSON.parse(localStorage.getItem('alfresco-user')!)]));
				} else {
					const utentiSitoList = resUtentiSito.list.entries
						.map((e: any) => e.entry.person)
						.filter((value: any, index: number, self: any[]) => self.findIndex((e: any) => e.id === value.id) === index);
					localStorage.setItem('alfresco-utenti-sito', JSON.stringify(utentiSitoList));
				}

				const utente = JSON.parse(localStorage.getItem('alfresco-user')!);
				const dataUltimaModificaPassword = utente.properties?.['kd:lastPasswordModifyDate'];
				if (dataUltimaModificaPassword && this.limiteScadenzaPassword && !saltaControlloScadenzaPassword) {
					const scadenzaPassword = new Date(dataUltimaModificaPassword);
					scadenzaPassword.setDate(scadenzaPassword.getDate() + this.limiteScadenzaPassword);
					if (new Date() > scadenzaPassword) {
						return throwError(() => 'password-scaduta');
					}
				}

				return of(utente);
			}),
		);
	}

	logout() {
		localStorage.clear();
		window.location.reload();
	}

	/**
	 *
	 * @param label - Etichetta del permesso, esempio formato: create-et_elaborati_tecnici
	 * @returns
	 *
	 * Controlla che l' utente abbia il permesso per eseguire un azione controllando in localstorage la presenza di un etichetta
	 */
	hasPermission(label: string) {
		const permessiString = localStorage.getItem('alfresco-groups');
		if (permessiString && permessiString !== 'undefined') {
			const permessi = JSON.parse(permessiString);
			return permessi.includes('GROUP_' + label);
		}
		return false;
	}

	controllaPassword(nuovaPassword: string, confermaNuovaPassword: string): boolean {
		if (nuovaPassword !== confermaNuovaPassword) {
			this.toastService.showError('Le password non coincidono');
			return false;
		}

		if (this.regexPassword && !new RegExp(this.regexPassword).test(nuovaPassword)) {
			this.toastService.showError(this.messaggioErroreRegexLogin);
			return false;
		}

		return true;
	}
}
